import { useEffect, useMemo, useState } from 'react'
import {
  CloudSun, Wind, Drop, Tree, Recycle, ShieldCheck, Warning,
  FileCsv, FilePdf, ArrowsClockwise, CheckCircle, Info
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from '../esg/exportHelpers'

const ACCENT = {
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A', slate: '#7A8FA0', charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF', borderRadius: '8px', padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8',
}

// Mapeo de cada estándar ESRS a indicadores reales del proyecto
const STANDARDS = [
  {
    id: 'E1', name: 'Cambio climático', icon: CloudSun, accent: ACCENT.olive,
    description: 'ESRS E1 — Mitigación + adaptación al cambio climático. Plan de transición climática, emisiones de GEI Scope 1/2/3, intensidad energética.',
    indicators: [
      { code: 'E1-1',   name: 'Plan de transición climática (1,5 °C)',        src: 'policy', policy: true },
      { code: 'E1-2',   name: 'Políticas relacionadas con el cambio climático', src: 'policy', policy: true },
      { code: 'E1-3',   name: 'Acciones y recursos (mitigación / adaptación)', src: 'iros' },
      { code: 'E1-4',   name: 'Objetivos GEI / Plan de transición',           src: 'gestión' },
      { code: 'E1-5',   name: 'Consumo energía / Mix renovable',              src: 'sensors-ene1' },
      { code: 'E1-6',   name: 'Emisiones brutas Scope 1/2/3',                 src: 'sensors-ene1-mat1-d14' },
      { code: 'E1-7',   name: 'Eliminaciones GEI / Créditos carbono',         src: 'manual' },
      { code: 'E1-8',   name: 'Precio interno del carbono',                   src: 'manual' },
      { code: 'E1-9',   name: 'Riesgos físicos y de transición climáticos',   src: 'iros' },
    ],
  },
  {
    id: 'E2', name: 'Contaminación', icon: Wind, accent: ACCENT.slate,
    description: 'ESRS E2 — Contaminación de aire, agua, suelo. Sustancias preocupantes (SVHC). Microplásticos.',
    indicators: [
      { code: 'E2-1', name: 'Políticas relacionadas con la contaminación',   src: 'policy', policy: true },
      { code: 'E2-4', name: 'Emisiones contaminantes al aire (NOx)',        src: 'breeam-cont2' },
      { code: 'E2-5', name: 'Sustancias preocupantes (SVHC, REACH)',        src: 'breeam-mat3-mat4' },
      { code: 'E2-6', name: 'Efectos financieros (multas, remediación)',    src: 'manual' },
    ],
  },
  {
    id: 'E3', name: 'Agua y recursos marinos', icon: Drop, accent: ACCENT.olive,
    description: 'ESRS E3 — Captación, consumo y descarga de agua. Estrés hídrico de la zona. Aguas marinas.',
    indicators: [
      { code: 'E3-1', name: 'Políticas relacionadas con el agua',           src: 'policy', policy: true },
      { code: 'E3-4', name: 'Captación y consumo de agua',                  src: 'sensors-ag1' },
      { code: 'E3-5', name: 'Reutilización y reciclado',                    src: 'sensors' },
      { code: 'E3-6', name: 'Áreas de estrés hídrico',                      src: 'wri-aqueduct' },
    ],
  },
  {
    id: 'E4', name: 'Biodiversidad y ecosistemas', icon: Tree, accent: ACCENT.olive,
    description: 'ESRS E4 — Impacto en hábitats, especies, áreas protegidas. Plan de protección de biodiversidad.',
    indicators: [
      { code: 'E4-2', name: 'Políticas de biodiversidad y ecosistemas',     src: 'policy', policy: true },
      { code: 'E4-4', name: 'Plan de transición y protección biodiversidad',src: 'breeam-use' },
      { code: 'E4-5', name: 'Indicadores de impacto en biodiversidad',      src: 'breeam-use' },
    ],
  },
  {
    id: 'E5', name: 'Uso de recursos y economía circular', icon: Recycle, accent: ACCENT.amber,
    description: 'ESRS E5 — Materiales primarios vs reciclados, residuos, producto y embalaje circular.',
    indicators: [
      { code: 'E5-1', name: 'Políticas de uso de recursos y circular',      src: 'policy', policy: true },
      { code: 'E5-4', name: 'Entradas de recursos (% reciclado)',           src: 'circular-ci' },
      { code: 'E5-5', name: 'Salidas / Residuos generados',                 src: 'breeam-rsd1-sensors' },
    ],
  },
]

// Códigos disclosure que se rellenan manualmente (bloques cualitativos)
const POLICY_CODES = new Set(['E1-1','E1-2','E2-1','E3-1','E4-2','E5-1'])

function catPct(evaluations, BREEAM_CRITERIA, ...catNames) {
  let obtained = 0, max = 0
  catNames.forEach(catName => {
    const cat = BREEAM_CRITERIA?.[catName]
    if (!cat) return
    Object.entries(cat.criteria).forEach(([code, c]) => {
      const ev = evaluations?.[`${catName}-${code}`]
      max += (ev?.points_max ?? c.points ?? 0)
      obtained += (ev?.puntos_auditoria ?? ev?.points_obtained) || 0
    })
  })
  return max === 0 ? null : { pct: Math.round((obtained / max) * 100), obtained, max }
}

function parseJ(ev) {
  if (!ev?.justificacion) return null
  try { return JSON.parse(ev.justificacion) } catch { return null }
}

function evPts(ev) {
  if (!ev) return null
  return (ev.puntos_auditoria ?? ev.points_obtained) ?? null
}

// E2-4: NOx concreto desde CNT 2 (calculadora oficial o tabla_comparativa)
function extractCNT2(evaluations) {
  const ev = evaluations['Contaminación-CNT 2']
  if (!ev) return null
  const j = parseJ(ev)
  let noxValue = null
  if (Array.isArray(j?.tabla_comparativa)) {
    const noxRow = j.tabla_comparativa.find(r => (r.parametro || '').toLowerCase().includes('nox'))
    if (noxRow) noxValue = noxRow.valor_proyecto || noxRow.valor_obtenido
  }
  return { pts: evPts(ev) ?? 0, max: ev.points_max ?? 3, noxValue }
}

// E2-5: % sourcing responsable desde MAT 3 calculadora
function extractMAT3(evaluations) {
  const ev = evaluations['Materiales-MAT 3']
  if (!ev) return null
  const j = parseJ(ev)
  const calc = j?.mat3_calculadora
  return {
    pts: evPts(ev) ?? 0, max: ev.points_max ?? 3,
    pct: calc?.pct ?? null,
    elements: calc?.presentElements ?? null,
  }
}

// E4-4/E4-5: extracción USE 4 + USE 5 (recomendaciones ISMYP, PGBE años, ECA)
function extractUSE45(evaluations) {
  const use4 = evaluations['Uso del Suelo y Ecología-USE 4']
  const use5 = evaluations['Uso del Suelo y Ecología-USE 5']
  const j4 = parseJ(use4)
  const j5 = parseJ(use5)
  // Contar recomendaciones ecológicas ISMYP (patrón "(1) ... (2) ..." o "(1)..." en subcriteria)
  const recText = j4?.subcriteria?.map(s => s?.justificacion || '').join(' ') || ''
  const recNums = new Set()
  recText.replace(/\((\d+)\)/g, (_, n) => recNums.add(parseInt(n)))
  const recCount = recNums.size || null
  // Duración PGBE
  const pgbeText = j5?.subcriteria?.map(s => s?.justificacion || '').join(' ') || ''
  const yMatch = pgbeText.match(/(cinco|diez|\d+)\s+años?/i)
  const yearsMap = { cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10 }
  let pgbeYears = null
  if (yMatch) pgbeYears = yearsMap[yMatch[1].toLowerCase()] ?? parseInt(yMatch[1])
  return {
    pts4: evPts(use4) ?? 0, use4Max: use4?.points_max ?? 3,
    pts5: evPts(use5) ?? 0, use5Max: use5?.points_max ?? 2,
    recCount, pgbeYears,
  }
}

function status(level) {
  if (level === 'cumple') return { color: ACCENT.olive, label: 'Reportable', Icon: CheckCircle }
  if (level === 'parcial') return { color: ACCENT.amber, label: 'Parcial',     Icon: Warning }
  return { color: '#B0A89E', label: 'Pendiente', Icon: Warning }
}

function PolicyEditor({ code, name, initial, onSave, onCancel }) {
  const [adopted, setAdopted] = useState(initial?.source_data?.adopted ?? false)
  const [adoptedDate, setAdoptedDate] = useState(initial?.source_data?.adopted_date || initial?.snapshot_date || '')
  const [scope, setScope] = useState(initial?.source_data?.scope || '')
  const [responsible, setResponsible] = useState(initial?.source_data?.responsible || '')
  const [stakeholders, setStakeholders] = useState(initial?.source_data?.stakeholders_consulted || '')
  const [reviewFreq, setReviewFreq] = useState(initial?.source_data?.review_frequency || 'anual')
  const [text, setText] = useState(initial?.value_text || '')
  const [saving, setSaving] = useState(false)
  const handleSave = async () => {
    setSaving(true)
    await onSave({
      code, name,
      value_text: text,
      source_data: { adopted, adopted_date: adoptedDate || null, scope, responsible, stakeholders_consulted: stakeholders, review_frequency: reviewFreq },
      snapshot_date: adoptedDate || null,
    })
    setSaving(false)
  }
  const input = { padding: '6px 9px', fontSize: '0.78rem', border: '1px solid #DDD5C8', borderRadius: 4, background: '#FFFFFF', fontFamily: 'inherit' }
  const label = { fontSize: '0.7rem', fontWeight: 600, color: '#5C4A3A', marginBottom: 3, display: 'block' }
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(20,15,10,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem 1.75rem', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace" }}>ESRS {code}</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2C2017', marginTop: 3 }}>{name}</div>
          <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: 4, lineHeight: 1.4 }}>Documenta la política adoptada por el promotor u operador para este disclosure ESRS. Los datos se guardan en <code>esg_snapshots</code> (framework: csrd) y se citan en el informe CSRD PDF/CSV.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={label}>¿Política formalmente adoptada?</label>
            <select value={adopted ? 'si' : 'no'} onChange={e => setAdopted(e.target.value === 'si')} style={input}>
              <option value="no">No · en preparación</option>
              <option value="si">Sí · adoptada</option>
            </select>
          </div>
          <div>
            <label style={label}>Fecha adopción</label>
            <input type="date" value={adoptedDate} onChange={e => setAdoptedDate(e.target.value)} style={input} />
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={label}>Alcance</label>
          <input type="text" value={scope} onChange={e => setScope(e.target.value)}
            placeholder="Ej.: Todas las operaciones del grupo · Solo activo XYZ · Cadena de valor upstream"
            style={{ ...input, width: '100%' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={label}>Responsable</label>
            <input type="text" value={responsible} onChange={e => setResponsible(e.target.value)}
              placeholder="Ej.: Chief Sustainability Officer" style={input} />
          </div>
          <div>
            <label style={label}>Frecuencia de revisión</label>
            <select value={reviewFreq} onChange={e => setReviewFreq(e.target.value)} style={input}>
              <option value="anual">Anual</option>
              <option value="bienal">Bienal</option>
              <option value="quinquenal">Quinquenal</option>
              <option value="ad_hoc">Ad hoc</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={label}>Stakeholders consultados</label>
          <input type="text" value={stakeholders} onChange={e => setStakeholders(e.target.value)}
            placeholder="Ej.: Comité ambiental, empleados, comunidad local, inversores"
            style={{ ...input, width: '100%' }} />
        </div>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={label}>Contenido de la política</label>
          <textarea value={text} onChange={e => setText(e.target.value)}
            rows={7}
            placeholder="Resume el contenido de la política: objetivos, compromisos, procesos, indicadores de seguimiento, gobernanza…"
            style={{ ...input, width: '100%', resize: 'vertical', minHeight: 140, lineHeight: 1.5 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid #DDD5C8', color: '#5C4A3A', borderRadius: 5, padding: '7px 14px', fontSize: '0.78rem', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ background: ACCENT.olive, border: 'none', color: '#FFFFFF', borderRadius: 5, padding: '7px 14px', fontSize: '0.78rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Guardando…' : 'Guardar política'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CSRDReport({ project, BREEAM_CRITERIA }) {
  const [sensors, setSensors] = useState(null)
  const [ci, setCi] = useState(null)
  const [iros, setIros] = useState([])
  const [iroSettings, setIroSettings] = useState(null)
  const [policies, setPolicies] = useState({})   // { 'E1-1': {value_text, source_data, snapshot_date, id}, ... }
  const [editingPolicy, setEditingPolicy] = useState(null)  // {code, name}
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadLive = async () => {
    if (!project?.id) return
    setLoading(true); setError(null)
    const [
      { data: s, error: e1 },
      { data: c, error: e2 },
      { data: iroRows },
      { data: iroSet },
      { data: pols },
    ] = await Promise.all([
      supabase.from('building_sensors').select('*').eq('project_id', project.id).order('reading_year', { ascending: false }),
      supabase.from('esg_snapshots').select('value_numeric, created_at').eq('project_id', project.id).eq('framework', 'circular').eq('indicator_code', 'DGNB_CI').order('created_at', { ascending: false }).limit(1),
      supabase.from('operational_iros').select('*').eq('project_id', project.id).neq('status', 'dismissed').neq('status', 'closed'),
      supabase.from('operational_iro_settings').select('*').eq('project_id', project.id).maybeSingle(),
      supabase.from('esg_snapshots').select('id, indicator_code, indicator_name, value_text, source_data, snapshot_date, updated_at:created_at').eq('project_id', project.id).eq('framework', 'csrd').in('indicator_code', [...POLICY_CODES]),
    ])
    setLoading(false)
    // Indexar políticas por code (nos quedamos con el snapshot más reciente por code)
    if (pols) {
      const byCode = {}
      pols.forEach(p => {
        const prev = byCode[p.indicator_code]
        if (!prev || (p.snapshot_date || '') > (prev.snapshot_date || '')) byCode[p.indicator_code] = p
      })
      setPolicies(byCode)
    }
    // Ignoramos los errores de operational_iros / operational_iro_settings porque
    // pueden fallar si las tablas no existen aún en proyectos antiguos.
    if (e1 || e2) { setError((e1 || e2).message); return }
    setIros(iroRows || [])
    setIroSettings(iroSet || null)
    if (s?.length) {
      const yMap = new Map()
      s.forEach(r => {
        const a = yMap.get(r.reading_year) || { year: r.reading_year, kwh: 0, kwh_renew: 0, electricity_kwh: 0, gas_kwh: 0, m3: 0, m3_recl: 0, waste_t: 0, waste_recycled_t: 0, scope1: 0, scope2: 0, scope3: 0, n: 0 }
        const totalThisMonth = +(r.energy_kwh_total || 0) || (
          +(r.electricity_kwh || 0) + +(r.gas_kwh || 0) + +(r.district_heat_kwh || 0) + +(r.energy_kwh_renewable || 0)
        )
        a.kwh += totalThisMonth
        a.kwh_renew += +(r.energy_kwh_renewable || 0)
        a.electricity_kwh += +(r.electricity_kwh || 0)
        a.gas_kwh += +(r.gas_kwh || 0)
        a.m3 += +(r.water_m3_total || 0)
        a.m3_recl += +(r.water_m3_reclaimed || 0)
        a.waste_t += +(r.waste_tons_total || 0)
        a.waste_recycled_t += +(r.waste_tons_recycled || 0)
        a.scope1 += +(r.scope1_kgco2e || 0)
        a.scope2 += +(r.scope2_kgco2e || 0)
        a.scope3 += +(r.scope3_kgco2e || 0)
        a.n += 1
        yMap.set(r.reading_year, a)
      })
      const years = [...yMap.keys()].sort((a, b) => b - a)
      const y = yMap.get(years[0])
      // Si scope2 no se cargó directamente, calcularlo desde electricidad (factor REE 0,181)
      if (y.scope2 === 0 && y.electricity_kwh > 0) {
        y.scope2 = y.electricity_kwh * 0.181
        y.scope2_derived = true
      }
      // Scope 1 desde gas si está
      if (y.scope1 === 0 && y.gas_kwh > 0) {
        y.scope1 = y.gas_kwh * 0.20
        y.scope1_derived = true
      }
      setSensors(y)
    }
    setCi(c?.[0]?.value_numeric ?? null)
  }
  // loadLive tiene sus propias deps (project?.id) y no queremos que se
  // recree en cada render añadiéndolo como dep del efecto.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLive() }, [project?.id])

  const savePolicy = async ({ code, name, value_text, source_data, snapshot_date }) => {
    if (!project?.id) return
    const payload = {
      project_id: project.id,
      framework: 'csrd',
      indicator_code: code,
      indicator_name: name,
      value_text: value_text || null,
      source_data: source_data || null,
      snapshot_date: snapshot_date || new Date().toISOString().slice(0, 10),
      source_module: 'CSRDReport-PolicyEditor',
    }
    // Buscar el más reciente existente para actualizar; si no, insertar
    const existing = policies[code]
    let res
    if (existing?.id) {
      res = await supabase.from('esg_snapshots').update(payload).eq('id', existing.id).select().maybeSingle()
    } else {
      res = await supabase.from('esg_snapshots').insert(payload).select().maybeSingle()
    }
    if (res.error) { setError(res.error.message); return }
    setPolicies(prev => ({ ...prev, [code]: res.data }))
    setEditingPolicy(null)
  }

  const evaluations = useMemo(() => project?.evaluations || {}, [project?.evaluations])
  const ene1J = useMemo(() => parseJ(evaluations['Energía-ENE 1']), [evaluations])
  const mat1J = useMemo(() => parseJ(evaluations['Materiales-MAT 1']), [evaluations])
  const rsd1J = useMemo(() => parseJ(evaluations['Residuos-RSD 1']), [evaluations])
  const cont2 = useMemo(() => catPct(evaluations, BREEAM_CRITERIA, 'Contaminación'), [evaluations, BREEAM_CRITERIA])
  const mat   = useMemo(() => catPct(evaluations, BREEAM_CRITERIA, 'Materiales'), [evaluations, BREEAM_CRITERIA])
  const usePc = useMemo(() => catPct(evaluations, BREEAM_CRITERIA, 'Uso del Suelo y Ecología'), [evaluations, BREEAM_CRITERIA])
  const agua  = useMemo(() => catPct(evaluations, BREEAM_CRITERIA, 'Agua'), [evaluations, BREEAM_CRITERIA])
  const gest  = useMemo(() => catPct(evaluations, BREEAM_CRITERIA, 'Gestión'), [evaluations, BREEAM_CRITERIA])

  const indicatorValues = useMemo(() => {
    const ene1pts = ene1J?.ene1_calculadora?.totalPts ?? ene1J?.ene1_calculadora?.pts ?? null
    const co2 = ene1J?.ene1_calculadora?.zonas?.length
      ? (() => {
          const z = ene1J.ene1_calculadora.zonas.filter(z => parseFloat(z.m2) > 0)
          if (!z.length) return null
          const m2 = z.reduce((s, x) => s + parseFloat(x.m2), 0)
          return z.reduce((s, x) => s + parseFloat(x.co2_eval || 0) * parseFloat(x.m2), 0) / m2
        })()
      : null
    const m2Total = ene1J?.ene1_calculadora?.zonas?.reduce((s, z) => s + (parseFloat(z.m2) || 0), 0) || project?.surface_m2 || null

    // Extraer IROs por detector para enriquecer indicadores concretos
    const findIro = (id) => iros.find(i => i.detector_id === id)
    const d13 = findIro('D13.taxonomia_7_7')   // Oportunidad Taxonomía 7.7
    const d14 = findIro('D14.performance_gap') // Impacto E1-6 Performance Gap
    const d12 = findIro('D12.excedente_fv')    // Oportunidad E1-3 excedente FV
    const d7  = findIro('D7.eu_ets')           // Riesgo transición E1-9
    const d8  = findIro('D8.riesgo_zbe')       // Riesgo transición E1-9
    const d10 = findIro('D10.calor_cronico')   // Riesgo físico crónico E1-9
    const d11 = findIro('D11.riesgo_inundacion') // Riesgo físico agudo E1-9

    // Componer E1-3 (acciones y recursos) desde oportunidades detectadas
    const e13Parts = []
    if (d13) e13Parts.push(`Taxonomía UE 7.7: ALINEADO (severidad ${d13.severity}/5)`)
    if (d12) e13Parts.push(`Excedente FV monetizable: ${d12.financial_estimate_eur ? '~' + Math.round(d12.financial_estimate_eur).toLocaleString('es-ES') + ' €/año' : 'detectado'}`)

    // Componer E1-9 (riesgos físicos y transición) desde IROs
    const e19Risks = []
    if (d7)  e19Risks.push(`EU ETS: ${d7.financial_estimate_eur ? '~' + Math.round(d7.financial_estimate_eur).toLocaleString('es-ES') + ' €/año' : 'exposición sev ' + d7.severity + '/5'} (transición)`)
    if (d8)  e19Risks.push(`ZBE ${iroSettings?.municipality || 'municipio'}: severidad ${d8.severity}/5 (transición)`)
    if (d10) e19Risks.push(`Calor crónico CDD: +${d10.detector_output?.excess_pct || '?'}% (físico crónico)`)
    if (d11) e19Risks.push(`Inundación ARPSI: sev ${d11.severity}/5 (físico agudo)`)
    // Verificación negativa de ARPSI (aunque no dispare D11) también es reportable
    const arpsiChecked = iroSettings?.arpsi_status && iroSettings.arpsi_status !== 'not_checked'
    const arpsiNoRisk = iroSettings?.arpsi_status === 'not_at_risk'

    // Helper para bloques manuales de políticas
    const policyValue = (code) => {
      const p = policies[code]
      if (!p?.value_text && !p?.source_data?.adopted) {
        return { value: '—', level: 'pendiente', detail: 'Sin política registrada. Pulsa "Editar" para cargar el documento adoptado, alcance y responsable.', editable: true }
      }
      const sd = p?.source_data || {}
      const parts = []
      if (sd.adopted_date || p.snapshot_date) parts.push(`adoptada ${sd.adopted_date || p.snapshot_date}`)
      if (sd.scope) parts.push(`alcance: ${sd.scope}`)
      if (sd.responsible) parts.push(`resp.: ${sd.responsible}`)
      const label = p.value_text ? p.value_text.slice(0, 90) + (p.value_text.length > 90 ? '…' : '') : 'Política registrada'
      return {
        value: label,
        level: sd.adopted ? 'cumple' : 'parcial',
        detail: parts.length ? parts.join(' · ') : 'Registrada sin metadata detallada. Ampliar para trazabilidad completa.',
        editable: true,
      }
    }

    return {
      // E1 · Políticas
      'E1-1': policyValue('E1-1'),
      'E1-2': policyValue('E1-2'),
      // E1 · Indicadores cuantitativos
      'E1-3': e13Parts.length > 0
        ? { value: e13Parts.join(' · '), level: 'cumple', detail: 'Oportunidades detectadas por el módulo IRO operativo (Taxonomía UE + FV in situ). Cita literal disponible en el detalle de cada IRO.' }
        : { value: '—', level: 'pendiente', detail: 'Ejecuta el módulo Monitorización → IROs para detectar oportunidades de mitigación (D12 excedente FV, D13 Taxonomía 7.7).' },
      'E1-4': (() => {
        const gst1J = parseJ(evaluations['Gestión-GST 1'])
        const gst5 = evaluations['Gestión-GST 5']
        const gst5pts = gst5 ? ((gst5.puntos_auditoria ?? gst5.points_obtained) || 0) : 0
        const seguimiento = gst1J?.gst1_subcriterios?.parte_seguimiento === true
        if (seguimiento && gst5pts >= 3) return { value: 'Plan seguimiento + LCC activo', level: 'cumple', detail: 'GST 1 Parte 3 (seguimiento operadores) + GST 5 (Life Cycle Costing) documentan objetivos de transición.' }
        if (seguimiento || gst5pts > 0) return { value: seguimiento ? 'GST 1 seguimiento activo' : `GST 5: ${gst5pts} pts LCC`, level: 'parcial', detail: 'Elementos parciales del plan de transición documentados. Añadir el otro para cumplimiento pleno.' }
        return gest?.pct != null
          ? { value: `${gest.pct}% Gestión BREEAM`, level: gest.pct >= 55 ? 'parcial' : 'pendiente', detail: 'Proxy débil: % puntos Gestión. Documentar GST 1 Parte 3 + GST 5 para un plan de transición formal.' }
          : { value: '—', level: 'pendiente', detail: 'Evalúa GST 1 (seguimiento operadores) y GST 5 (LCC).' }
      })(),
      'E1-5': sensors?.kwh != null
        ? { value: `${Math.round(sensors.kwh).toLocaleString()} kWh/año · ${sensors.kwh_renew ? Math.round(sensors.kwh_renew / sensors.kwh * 100) : 0}% renovable`,
            level: 'cumple', detail: `Sensores año ${sensors.year}. Consumo total y % renovable in situ.` }
        : ene1J?.ene1_calculadora?.zonas?.length > 0
          ? (() => {
              const z = ene1J.ene1_calculadora.zonas.filter(x => parseFloat(x.m2) > 0)
              const m2T = z.reduce((s, x) => s + parseFloat(x.m2), 0)
              const consEval = m2T > 0 ? z.reduce((s, x) => s + parseFloat(x.con_eval || 0) * parseFloat(x.m2), 0) / m2T : null
              return {
                value: consEval != null ? `${consEval.toFixed(1)} kWh/m²·año (ENE 1, diseño)${ene1pts != null ? ` · ${ene1pts}/15 pts` : ''}` : `BREEAM ENE 1: ${ene1pts ?? '—'}/15 pts`,
                level: 'parcial',
                detail: 'Intensidad energética de diseño ENE 1. Cargar building_sensors para reporte operacional real y % renovable.',
              }
            })()
          : { value: '—', level: 'pendiente', detail: 'Carga ENE 1 o building_sensors.' },
      'E1-6': sensors && (sensors.scope1 + sensors.scope2 + sensors.scope3) > 0
        ? { value: `${((sensors.scope1 + sensors.scope2 + sensors.scope3) / 1000).toFixed(1)} tCO₂e/año (S1+S2+S3)`,
            level: 'cumple',
            detail: `S1: ${(sensors.scope1 / 1000).toFixed(1)} · S2: ${(sensors.scope2 / 1000).toFixed(1)} · S3: ${(sensors.scope3 / 1000).toFixed(1)}.${d14 ? ' D14 detectado: gap real vs CEE +' + (d14.detector_output?.excess_pct || '?') + '%.' : ''}` }
        : co2 != null && m2Total
          ? { value: `≈ ${(co2 * m2Total / 1000).toFixed(1)} tCO₂e/año (estimado ENE 1)`, level: 'parcial', detail: 'Estimación desde calculadora ENE 1. Sin Scope 3.' }
          : { value: '—', level: 'pendiente', detail: 'Carga ENE 1 + building_sensors.' },
      'E1-7': { value: '—', level: 'pendiente', detail: 'No reportado — registrar créditos / compensaciones manualmente.' },
      'E1-8': { value: '—', level: 'pendiente', detail: 'No reportado — registrar precio interno carbono si aplica.' },
      'E1-9': e19Risks.length > 0 || arpsiChecked
        ? { value: e19Risks.length > 0 ? `${e19Risks.length} riesgo${e19Risks.length === 1 ? '' : 's'} identificado${e19Risks.length === 1 ? '' : 's'}` : (arpsiNoRisk ? 'Inundación verificada: NO en riesgo' : 'ARPSI comprobado'),
            level: e19Risks.length > 0 ? 'cumple' : 'parcial',
            detail: [...e19Risks, arpsiNoRisk ? 'ARPSI MITECO: activo NO clasificado en zona de riesgo (verificado)' : null].filter(Boolean).join(' · ') }
        : { value: 'Cualitativo', level: 'parcial', detail: 'Ejecuta el módulo IRO (D7 EU ETS, D8 ZBE, D10 calor crónico, D11 inundación) para poblar automáticamente esta sección.' },
      // E2 · Política
      'E2-1': policyValue('E2-1'),
      // E2 · Indicadores cuantitativos
      'E2-4': (() => {
        const c2 = extractCNT2(evaluations)
        if (c2?.noxValue) {
          return { value: `NOx ${c2.noxValue}`, level: 'cumple',
                   detail: `BREEAM CNT 2 ${c2.pts}/${c2.max} pts · valor NOx del CEE del proyecto. Para cumplimiento pleno E2-4 falta reportar PM10, PM2.5, VOCs y contaminación de agua/suelo.` }
        }
        if (c2?.pts > 0) {
          return { value: `CNT 2: ${c2.pts}/${c2.max} pts (NOx)`,
                   level: c2.pts >= 2 ? 'cumple' : 'parcial',
                   detail: 'BREEAM CNT 2 cubre NOx. Cargar tabla_comparativa con valor concreto y reportar PM, VOCs, agua y suelo para E2-4 pleno.' }
        }
        return cont2?.pct != null
          ? { value: `${cont2.pct}% CONT BREEAM`, level: cont2.pct >= 55 ? 'parcial' : 'pendiente', detail: 'Proxy débil: % Contaminación. Evalúa CNT 2 para NOx específico.' }
          : { value: '—', level: 'pendiente', detail: 'Evalúa CNT 2 (NOx).' }
      })(),
      'E2-5': (() => {
        const m3 = extractMAT3(evaluations)
        if (m3?.pct != null) {
          return { value: `MAT 3: ${m3.pct.toFixed(1)}% sourcing responsable${m3.elements ? ` (${m3.elements} elementos)` : ''}`,
                   level: m3.pct >= 36 ? 'cumple' : 'parcial',
                   detail: 'BREEAM MAT 3 documenta aprovisionamiento responsable (BES 6001, ISO 14001, FSC, PEFC, EPDs). Para cumplimiento pleno E2-5 falta inventario específico de SVHC del Anexo XIV REACH.' }
        }
        if (m3?.pts > 0) {
          return { value: `MAT 3: ${m3.pts}/${m3.max} pts`, level: 'parcial', detail: 'Cargar mat3_calculadora con % para reporte E2-5 más preciso.' }
        }
        return mat?.pct != null
          ? { value: `${mat.pct}% MAT BREEAM`, level: 'parcial', detail: 'Proxy débil: % Materiales. Evalúa MAT 3 para SVHC.' }
          : { value: '—', level: 'pendiente', detail: 'Evalúa MAT 3.' }
      })(),
      'E2-6': { value: '—', level: 'pendiente', detail: 'No aplica salvo multas/remediación específicas.' },
      // E3 · Política
      'E3-1': policyValue('E3-1'),
      // E3 · Indicadores cuantitativos
      'E3-4': sensors?.m3 != null
        ? { value: `${Math.round(sensors.m3).toLocaleString()} m³/año`,
            level: 'cumple', detail: `Sensores año ${sensors.year}.` }
        : agua?.pct != null
          ? { value: `BREEAM Agua ${agua.pct}%`, level: 'parcial', detail: 'Proxy diseño. Cargar building_sensors para reporte real.' }
          : { value: '—', level: 'pendiente', detail: 'Carga AG 1 o building_sensors.' },
      'E3-5': sensors?.m3 != null
        ? { value: `${sensors.m3 ? Math.round(sensors.m3_recl / sensors.m3 * 100) : 0}% reutilizada`,
            level: sensors.m3_recl > 0 ? 'cumple' : 'parcial', detail: 'Sensores building_sensors.' }
        : { value: '—', level: 'pendiente', detail: 'Carga building_sensors.' },
      'E3-6': (() => {
        const city = project?.municipality || project?.city || project?.location || null
        const region = ((project?.province || project?.region || '') + ' ' + (city || '')).toLowerCase()
        if (!city && !region.trim()) {
          return { value: '—', level: 'pendiente', detail: 'Sin ubicación del proyecto cargada. Rellena project.city / project.province para clasificar contra WRI Aqueduct.' }
        }
        // Clasificación regional simplificada (WRI Aqueduct para España)
        const highStress = ['tenerife','gran canaria','fuerteventura','lanzarote','las palmas','santa cruz','murcia','almería','alicante','málaga','sevilla','córdoba','jaén','granada','huelva','cádiz','albacete','guía de isora']
        const medStress  = ['barcelona','tarragona','lleida','girona','valencia','castellón','baleares','madrid','toledo','ciudad real','cuenca','zaragoza','sant cugat']
        const lowStress  = ['a coruña','lugo','pontevedra','ourense','asturias','cantabria','vizcaya','guipúzcoa','álava','navarra','la rioja','burgos','soria','león','palencia','valladolid','salamanca','ávila','segovia','cáceres','badajoz','huesca','teruel']
        let level = 'parcial', label = 'medio'
        if (highStress.some(k => region.includes(k))) { level = 'parcial'; label = 'alto' }
        else if (lowStress.some(k => region.includes(k))) { level = 'cumple'; label = 'bajo' }
        else if (medStress.some(k => region.includes(k))) { level = 'parcial'; label = 'medio' }
        else { label = 'sin clasificar' ; level = 'pendiente' }
        return { value: `${city || project?.province} · estrés hídrico ${label}`, level,
                 detail: 'Clasificación regional aproximada. Recomendado verificar contra WRI Aqueduct y planes hidrológicos de cuenca (RD 1/2016).' }
      })(),
      // E4 · Política
      'E4-2': policyValue('E4-2'),
      // E4 · Indicadores cuantitativos
      'E4-4': (() => {
        const u = extractUSE45(evaluations)
        if (u.pts5 > 0 && u.pgbeYears) {
          return { value: `PGBE ${u.pgbeYears} años · USE 4: ${u.pts4}/${u.use4Max}, USE 5: ${u.pts5}/${u.use5Max}`,
                   level: (u.pts4 >= 2 && u.pts5 >= 1) ? 'cumple' : 'parcial',
                   detail: `Plan de Gestión Biodiversidad Emplazamiento (${u.pgbeYears} años) firmado por ECA cualificado${u.recCount ? ` · ${u.recCount} recomendaciones ISMYP` : ''}. Cumple E4-4 en su dimensión BREEAM.` }
        }
        if (u.pts4 > 0 || u.pts5 > 0) {
          return { value: `USE 4: ${u.pts4}/${u.use4Max} · USE 5: ${u.pts5}/${u.use5Max}`,
                   level: 'parcial',
                   detail: 'Plan de biodiversidad parcial. Añadir PGBE (USE 5) o ISMYP (USE 4) para cumplimiento pleno E4-4.' }
        }
        return usePc?.pct != null
          ? { value: `${usePc.pct}% USE BREEAM`, level: usePc.pct >= 55 ? 'parcial' : 'pendiente', detail: 'Proxy débil. Evalúa USE 4 + USE 5 para PGBE + ISMYP.' }
          : { value: '—', level: 'pendiente', detail: 'Evalúa USE 4 (ISMYP) y USE 5 (PGBE).' }
      })(),
      'E4-5': (() => {
        const u = extractUSE45(evaluations)
        if (u.pts4 >= 2 || u.pts5 >= 1) {
          return { value: `USE 4: ${u.pts4}/${u.use4Max} mejora ecológica${u.recCount ? ` · ${u.recCount} rec ISMYP` : ''}`,
                   level: (u.pts4 >= 2 && u.pts5 >= 1) ? 'cumple' : 'parcial',
                   detail: 'Cuantificar ha impactadas/restauradas y especies UICN afectadas (a partir del ISMYP y del Informe Ecológico Post-Construcción) para reporte E4-5 pleno.' }
        }
        return usePc?.pct != null
          ? { value: `${usePc.obtained}/${usePc.max} pts USE`, level: 'parcial', detail: 'Cuantificar especies + área restaurada.' }
          : { value: '—', level: 'pendiente', detail: 'Evalúa USE 4 + USE 5.' }
      })(),
      // E5 · Política
      'E5-1': policyValue('E5-1'),
      // E5 · Indicadores cuantitativos
      'E5-4': ci != null
        ? { value: `CI = ${ci.toFixed(2)} (DGNB)`, level: ci >= 0.45 ? 'cumple' : 'parcial', detail: 'Building Resource Passport — % material reciclado integrado.' }
        : mat1J ? { value: 'MAT 1 con LCA OneClick disponible', level: 'parcial', detail: 'Calcula CI en módulo Economía Circular para activar.' }
        : { value: '—', level: 'pendiente', detail: 'Llena Inventario en Economía Circular.' },
      'E5-5': sensors?.waste_t != null
        ? { value: `${sensors.waste_t.toFixed(1)} t/año · ${sensors.waste_t ? Math.round(sensors.waste_recycled_t / sensors.waste_t * 100) : 0}% reciclados`,
            level: 'cumple', detail: `Sensores año ${sensors.year}.` }
        : rsd1J?.rsd1_calculadora?.tasa_desvio != null
          ? { value: `RSD 1: ${rsd1J.rsd1_calculadora.tasa_desvio}% desvío`, level: 'cumple', detail: 'Datos de obra (construcción). Reporte CSRD requiere también ciclo operacional.' }
          : { value: '—', level: 'pendiente', detail: 'Carga RSD 1 o building_sensors.' },
    }
  }, [sensors, ci, ene1J, mat1J, rsd1J, cont2, mat, usePc, agua, gest, iros, iroSettings, project?.surface_m2, project?.municipality, project?.city, project?.location, project?.province, project?.region, evaluations, policies])

  // Resumen IROs por tipo (para bloque nuevo)
  const iroSummary = useMemo(() => {
    const impact = iros.filter(i => i.type === 'impact')
    const risk   = iros.filter(i => i.type === 'risk')
    const opportunity = iros.filter(i => i.type === 'opportunity')
    const totalEur = iros.reduce((s, i) => s + (Number(i.financial_estimate_eur) || 0), 0)
    return { impact, risk, opportunity, totalEur, all: iros }
  }, [iros])

  // Doble materialidad simplificada
  const materialityRows = STANDARDS.map(s => {
    const ivs = s.indicators.map(i => indicatorValues[i.code])
    const cumple = ivs.filter(i => i.level === 'cumple').length
    const total = ivs.length
    const pct = Math.round((cumple / total) * 100)
    return { id: s.id, name: s.name, cumple, total, pct, impact: pct >= 50 ? 'Material' : 'Por evaluar' }
  })

  const handleCSV = () => {
    const rows = []
    STANDARDS.forEach(s => {
      s.indicators.forEach(i => {
        const v = indicatorValues[i.code]
        rows.push({
          standard: s.id, estandar_nombre: s.name,
          indicador_codigo: i.code, indicador_nombre: i.name,
          valor: v.value, estado: v.level, fuente: i.src,
        })
      })
    })
    downloadCSV(rows, `CSRD_ESRS_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const handlePDF = () => {
    const sections = [
      {
        title: 'Doble materialidad simplificada',
        cols: ['Estándar', 'Indicadores reportables', 'Cobertura', 'Materialidad'],
        rows: materialityRows.map(r => [`${r.id}. ${r.name}`, `${r.cumple}/${r.total}`, `${r.pct}%`, r.impact]),
      },
      ...STANDARDS.map(s => ({
        title: `${s.id} — ${s.name}`,
        subtitle: s.description,
        cols: ['Código', 'Indicador', 'Valor', 'Estado'],
        rows: s.indicators.map(i => {
          const v = indicatorValues[i.code]
          return [i.code, i.name, v.value, v.level === 'cumple' ? 'Reportable' : v.level === 'parcial' ? 'Parcial' : 'Pendiente']
        }),
      })),
    ]
    openPrintWindow({ title: 'CSRD / ESRS E1-E5 — Informe ambiental', projectName: project?.name || 'Proyecto', sections })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>
            Directiva CSRD UE 2022/2464 · Estándares ESRS
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>Informe de Sostenibilidad Corporativa</div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>ESRS E1 (clima) · E2 (contaminación) · E3 (agua) · E4 (biodiversidad) · E5 (circular)</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={loadLive} disabled={loading} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <ArrowsClockwise size={13} /> {loading ? 'Cargando…' : 'Refrescar'}
          </button>
          <button onClick={handleCSV} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <FileCsv size={13} /> CSV
          </button>
          <button onClick={handlePDF} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <FilePdf size={13} /> PDF
          </button>
        </div>
      </div>

      {error && <div style={{ color: ACCENT.coral, fontSize: '0.8rem' }}>Error: {error}</div>}

      {/* Nota de alcance */}
      <div style={{ background: '#FBF7EF', border: `1px solid #E0D5BC`, borderLeft: `3px solid ${ACCENT.amber}`, borderRadius: '6px', padding: '0.85rem 1.1rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: ACCENT.amber, flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '0.75rem', color: '#5C4A3A', lineHeight: 1.5 }}>
            <strong style={{ color: '#2C2017' }}>Alcance del módulo:</strong> este informe cubre los <strong>ESRS E1-E5 aplicables al activo</strong> (edificio). Los ESRS <strong>S1-S4</strong> (propios trabajadores, cadena de valor, comunidades, consumidores) y <strong>G1</strong> (conducta empresarial) son responsabilidad del <strong>reporte corporativo</strong> del titular u operador y se rellenan al nivel de grupo, no en la app BREEAM. El asesor aporta aquí datos técnicos que el promotor/operador integrará en su Estado de Información No Financiera consolidado.
          </div>
        </div>
      </div>

      {/* IROs operativos detectados — integración con módulo Monitorización */}
      {iroSummary.all.length > 0 && (
        <div style={{ ...CARD, borderLeft: `4px solid ${ACCENT.olive}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.7rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#2C2017' }}>IROs operativos detectados</h3>
              <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: 4 }}>
                Impactos, Riesgos y Oportunidades identificados por el módulo Monitorización (Fase 1 + Fase 2) que alimentan los datapoints ESRS E1-3, E1-6 y E1-9.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
            <div style={{ background: '#FDF5F3', border: `1px solid ${ACCENT.coral}33`, borderLeft: `3px solid ${ACCENT.coral}`, borderRadius: 5, padding: '0.4rem 0.7rem', minWidth: 130 }}>
              <div style={{ fontSize: '0.65rem', color: '#7A6A5A', textTransform: 'uppercase' }}>Impactos</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ACCENT.coral, fontFamily: "'IBM Plex Mono', monospace" }}>{iroSummary.impact.length}</div>
            </div>
            <div style={{ background: '#FFF6E5', border: `1px solid ${ACCENT.amber}33`, borderLeft: `3px solid ${ACCENT.amber}`, borderRadius: 5, padding: '0.4rem 0.7rem', minWidth: 130 }}>
              <div style={{ fontSize: '0.65rem', color: '#7A6A5A', textTransform: 'uppercase' }}>Riesgos</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ACCENT.amber, fontFamily: "'IBM Plex Mono', monospace" }}>{iroSummary.risk.length}</div>
            </div>
            <div style={{ background: '#F0F5EC', border: `1px solid ${ACCENT.olive}33`, borderLeft: `3px solid ${ACCENT.olive}`, borderRadius: 5, padding: '0.4rem 0.7rem', minWidth: 130 }}>
              <div style={{ fontSize: '0.65rem', color: '#7A6A5A', textTransform: 'uppercase' }}>Oportunidades</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ACCENT.olive, fontFamily: "'IBM Plex Mono', monospace" }}>{iroSummary.opportunity.length}</div>
            </div>
            {iroSummary.totalEur > 0 && (
              <div style={{ background: '#F5F0EA', border: `1px solid #DDD5C8`, borderLeft: `3px solid #4A4A4A`, borderRadius: 5, padding: '0.4rem 0.7rem', minWidth: 130 }}>
                <div style={{ fontSize: '0.65rem', color: '#7A6A5A', textTransform: 'uppercase' }}>Exposición €/año</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#2C2017', fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(iroSummary.totalEur).toLocaleString('es-ES')} €</div>
              </div>
            )}
          </div>
          <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Detector</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>ESRS</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Tipo</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>Severidad</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Impacto €/año</th>
              </tr>
            </thead>
            <tbody>
              {iros.map((r, i) => {
                const typeColor = r.type === 'impact' ? ACCENT.coral : r.type === 'risk' ? ACCENT.amber : ACCENT.olive
                const typeLabel = r.type === 'impact' ? 'Impacto' : r.type === 'risk' ? 'Riesgo' : 'Oportunidad'
                const detLabel = (r.detector_id || '').replace(/^D\d+\./, '').replace(/_/g, ' ')
                return (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#FFF' : '#FAF7F3', borderBottom: '1px solid #F0EBE2' }}>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#2C2017' }}>{r.detector_id?.split('.')[0]} · {detLabel}</td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: "'IBM Plex Mono', monospace", color: '#7A6A5A' }}>{r.esrs_datapoint || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: typeColor, fontWeight: 600 }}>{typeLabel}</td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", color: typeColor }}>{'●'.repeat(r.severity || 0)}{'○'.repeat(5 - (r.severity || 0))}</td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{r.financial_estimate_eur != null ? Math.round(r.financial_estimate_eur).toLocaleString('es-ES') + ' €' : 'n/a'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Doble materialidad */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.95rem', color: '#2C2017' }}>Doble materialidad simplificada</h3>
        <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Estándar</th>
              <th style={{ padding: '0.5rem' }}>Indicadores reportables</th>
              <th style={{ padding: '0.5rem' }}>Cobertura</th>
              <th style={{ padding: '0.5rem' }}>Materialidad</th>
            </tr>
          </thead>
          <tbody>
            {materialityRows.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #F0EBE2' }}>
                <td style={{ padding: '0.45rem 0.5rem', color: '#2C2017' }}>{r.id}. {r.name}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>{r.cumple}/{r.total}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center', color: r.pct >= 50 ? ACCENT.olive : ACCENT.amber, fontWeight: 600 }}>{r.pct}%</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>{r.impact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detalle por estándar */}
      {STANDARDS.map(s => {
        const SIcon = s.icon
        return (
          <div key={s.id} style={{ ...CARD, borderLeft: `4px solid ${s.accent}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '0.5rem' }}>
              <SIcon size={22} style={{ color: s.accent }} />
              <div>
                <div style={{ fontSize: '0.65rem', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ESRS {s.id}</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#2C2017' }}>{s.name}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#7A6A5A', marginBottom: '0.875rem', lineHeight: 1.5 }}>{s.description}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.625rem' }}>
              {s.indicators.map(i => {
                const v = indicatorValues[i.code]
                const st = status(v.level)
                const SIc = st.Icon
                const isPolicy = POLICY_CODES.has(i.code)
                return (
                  <div key={i.code} style={{ background: '#FAF7F3', border: `1px solid ${v.level === 'cumple' ? '#C8D8C0' : '#DDD5C8'}`, borderRadius: '6px', padding: '0.7rem 0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.65rem', fontFamily: "'IBM Plex Mono', monospace", color: '#7A6A5A' }}>{i.code}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.6rem', color: st.color, fontWeight: 600 }}>
                        <SIc size={11} weight={v.level === 'cumple' ? 'fill' : 'regular'} /> {st.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C2017', marginBottom: '0.25rem' }}>{i.name}</div>
                    <div style={{ fontSize: '0.85rem', fontFamily: "'IBM Plex Mono', monospace", color: st.color, marginBottom: '0.25rem' }}>{v.value}</div>
                    <div style={{ fontSize: '0.68rem', color: '#7A6A5A', fontStyle: 'italic', lineHeight: 1.4 }}>{v.detail}</div>
                    {isPolicy && (
                      <button onClick={() => setEditingPolicy({ code: i.code, name: i.name })}
                        style={{ marginTop: '0.5rem', background: 'transparent', border: `1px solid ${s.accent}`, color: s.accent, borderRadius: 4, padding: '3px 8px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}>
                        {policies[i.code] ? '✎ Editar política' : '+ Cargar política'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Editor de políticas */}
      {editingPolicy && (
        <PolicyEditor
          code={editingPolicy.code}
          name={editingPolicy.name}
          initial={policies[editingPolicy.code]}
          onCancel={() => setEditingPolicy(null)}
          onSave={savePolicy} />
      )}

      {/* Nota legal */}
      <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start' }}>
          <ShieldCheck size={16} style={{ color: '#7A6A5A', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '0.75rem', color: '#7A6A5A', margin: 0, lineHeight: '1.6' }}>
            <strong style={{ color: '#5C4A3A' }}>Alcance:</strong> los indicadores ESRS E1-E5 se calculan automáticamente desde <code>building_sensors</code>, <code>operational_iros</code> (módulo Monitorización → IROs), calculadoras BREEAM (ENE 1, MAT 1, RSD 1) y el Building Resource Passport DGNB. Los IROs alimentan directamente E1-3 (acciones), E1-6 (emisiones gap real) y E1-9 (riesgos físicos y de transición). La <em>doble materialidad</em> aquí mostrada es una versión técnica simplificada — el informe CSRD oficial requiere un análisis de materialidad de impacto + financiera más extenso (Comité ESG + entrevistas con stakeholders). Aplicable a partir de 2025 para grandes empresas según Directiva (UE) 2022/2464.
          </p>
        </div>
      </div>
    </div>
  )
}
